import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AssigneePicker } from './assignee-picker';
import { useTrade } from '@/lib/trade';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Mail, MapPin, Phone, ShoppingCart, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ErrorState,
  Skeleton,
  useToast,
} from '@/components/ui';
import { PriorityBadge, TaskStatusBadge } from './task-status';
import { CloseTaskDialog } from './close-task-dialog';
import { VisitCard } from './visit-card';
import { WorkPlanCard } from './work-plan-card';
import { linesFromChecklist, PurchaseOrderDialog } from '@/features/purchasing/purchase-orders-page';
import { useFeatures } from '@/lib/features';
import { request } from '@/lib/api';
import { taskDetailSchema, type ChecklistItem } from '@/lib/schemas';
import { useAuth, useCanEdit } from '@/lib/auth';
import { formatDateTime, formatRelative } from '@/lib/utils';

const SOURCE_LABEL = {
  EMAIL: 'source.email',
  MANUAL: 'source.manual',
  CUSTOMER_LINK: 'source.customerLink',
  QUOTE: 'source.quote',
} as const;

export function TaskDetailPage() {
  const { t } = useTranslation(['tasks', 'common']);
  const trade = useTrade();
  const { taskId } = useParams({ from: '/protected/tasks/$taskId' });
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const canEdit = useCanEdit();

  const task = useQuery({
    queryKey: ['task', taskId],
    queryFn: ({ signal }) => request(`/tasks/${taskId}`, { schema: taskDetailSchema, signal }),
  });

  // הצ'ק-ליסט נערך מקומית ונשלח רק בסגירה. עריכה אופטימית לכל
  // תיבה הייתה מייצרת בקשה לכל הקלקה, ובשטח הקליטה לא תמיד יציבה.
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [ordering, setOrdering] = useState(false);
  const features = useFeatures();
  useEffect(() => {
    if (task.data?.checklist) setChecklist(task.data.checklist);
  }, [task.data?.checklist]);

  const assign = useMutation({
    mutationFn: () => request(`/scheduling/${taskId}/assign`, { method: 'POST' }),
    onSuccess: async () => {
      toast.success(t('detail.assigned'));
      await qc.invalidateQueries({ queryKey: ['task', taskId] });
      await qc.invalidateQueries({ queryKey: ['tasks'] });
    },
    // השרת מחזיר סיבה קונקרטית ("אין טכנאי עם הכישור הנדרש") —
    // מציגים אותה במקום הודעה גנרית.
    onError: (err: Error) => toast.error(t('detail.assignFailed'), err.message),
  });

  if (task.isLoading) return <DetailSkeleton />;
  if (task.isError) return <ErrorState error={task.error} onRetry={() => void task.refetch()} />;
  if (!task.data) return null;

  const detail = task.data;
  const isOpen = detail.status !== 'CLOSED' && detail.status !== 'CANCELLED';
  const doneCount = checklist.filter((c) => c.done).length;

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <div>
        <Link
          to="/tasks"
          className="inline-flex items-center gap-1.5 text-xs text-fg-muted transition-colors hover:text-fg"
        >
          {/* החץ מצביע אחורה לפי כיוון הקריאה — ב-RTL הוא מתהפך ומצביע ימינה. */}
          <ArrowLeft className="size-3.5 rtl:-scale-x-100" aria-hidden />
          {t('detail.back')}
        </Link>

        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-fg">{detail.title}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <TaskStatusBadge status={detail.status} />
              <PriorityBadge priority={detail.priority} />
              <Badge tone="neutral" dot={false}>
                {t(SOURCE_LABEL[detail.source])}
              </Badge>
            </div>
          </div>

          {/* צפייה בלבד: בלי שיבוץ, הזמנה וסגירה. */}
          {isOpen && canEdit ? (
            <div className="flex gap-2">
              {!detail.assignedToUserId && can('OWNER', 'MANAGER') ? (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={assign.isPending}
                  onClick={() => assign.mutate()}
                >
                  <UserPlus aria-hidden />
                  {t('detail.assignWorker', { worker: trade.worker.one })}
                </Button>
              ) : null}
              {detail.kind === 'ADMIN' && features.has('purchaseOrders') ? (
                <Button variant="secondary" onClick={() => setOrdering(true)}>
                  <ShoppingCart aria-hidden />
                  {t('detail.purchaseOrder')}
                </Button>
              ) : null}
              <CloseTaskDialog taskId={detail.id} checklist={checklist} />
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          {detail.description ? (
            <Card>
              <CardHeader>
                <CardTitle>{t('detail.description')}</CardTitle>
              </CardHeader>
              <CardContent>
                {/* whitespace-pre-wrap — טקסט שמגיע ממייל מכיל שורות. */}
                <p className="whitespace-pre-wrap text-sm leading-(--leading-normal) text-fg-muted">
                  {detail.description}
                </p>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader className="flex items-center justify-between">
              <CardTitle>{t('detail.checklist')}</CardTitle>
              {checklist.length > 0 ? (
                <span className="tabular text-xs text-fg-subtle">
                  {doneCount} / {checklist.length}
                </span>
              ) : null}
            </CardHeader>
            {checklist.length === 0 ? (
              <CardContent>
                <p className="text-xs text-fg-subtle">{t('detail.checklistEmpty')}</p>
              </CardContent>
            ) : (
              <ul className="divide-y divide-border">
                {checklist.map((item, i) => {
                  const id = `check-${i}`;
                  return (
                    <li key={id} className="flex items-start gap-3 px-5 py-3">
                      <Checkbox
                        id={id}
                        checked={item.done}
                        disabled={!isOpen || !canEdit}
                        onCheckedChange={(v) =>
                          setChecklist((prev) =>
                            prev.map((c, j) => (j === i ? { ...c, done: v === true } : c)),
                          )
                        }
                        className="mt-0.5"
                      />
                      <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
                        <span className={item.done ? 'text-sm text-fg-subtle line-through' : 'text-sm text-fg'}>
                          {item.label}
                        </span>
                        {item.sku || item.priceCode ? (
                          <span className="mt-0.5 flex flex-wrap gap-1.5">
                            {item.sku ? (
                              <Badge tone="neutral" dot={false}>
                                <span className="ltr-inline">
                                  {item.sku}
                                  {item.qty ? ` ×${item.qty}` : ''}
                                </span>
                              </Badge>
                            ) : null}
                            {item.priceCode ? (
                              <Badge tone="info" dot={false}>
                                <span className="ltr-inline">
                                  {item.priceCode}
                                  {item.priceQty ? ` ×${item.priceQty}` : ''}
                                </span>
                              </Badge>
                            ) : null}
                          </span>
                        ) : null}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-5">
          <VisitCard task={detail} />
          <Card>
            <CardHeader>
              <CardTitle>{t('detail.customer')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5">
              <p className="text-sm font-medium text-fg">{detail.customer?.name ?? '—'}</p>
              {detail.customer?.phone ? (
                <a
                  href={`tel:${detail.customer.phone}`}
                  className="flex items-center gap-2 text-xs text-fg-muted transition-colors hover:text-accent"
                >
                  <Phone className="size-3.5 shrink-0" aria-hidden />
                  <span className="ltr-inline">{detail.customer.phone}</span>
                </a>
              ) : null}
              {detail.customer?.email ? (
                <a
                  href={`mailto:${detail.customer.email}`}
                  className="flex items-center gap-2 text-xs text-fg-muted transition-colors hover:text-accent"
                >
                  <Mail className="size-3.5 shrink-0" aria-hidden />
                  <span className="ltr-inline truncate">{detail.customer.email}</span>
                </a>
              ) : null}
              {detail.customer?.address ? (
                <p className="flex items-start gap-2 text-xs text-fg-muted">
                  <MapPin className="mt-px size-3.5 shrink-0" aria-hidden />
                  {detail.customer.address}
                </p>
              ) : null}
            </CardContent>
          </Card>

          {detail.originQuoteId ? <WorkPlanCard quoteId={detail.originQuoteId} currentTaskId={detail.id} /> : null}

          <Card>
            <CardHeader>
              <CardTitle>{t('detail.details')}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-2.5 text-xs">
                <Row label={t('detail.assignedTo')}>
                  {isOpen && can('OWNER', 'MANAGER') ? (
                    <AssigneePicker taskId={detail.id} assignedToUserId={detail.assignedToUserId ?? null} />
                  ) : (
                    (detail.assignedTo?.name ?? t('detail.unassigned'))
                  )}
                </Row>
                {detail.kind === 'ADMIN' ? (
                  <Row label={t('detail.type')}>
                    {t('detail.adminTask')}
                    {detail.dueAt ? t('detail.dueSuffix', { date: formatDateTime(detail.dueAt) }) : ''}
                  </Row>
                ) : (
                  <Row label={t('detail.jobType')}>{detail.jobTypeTemplate?.name ?? '—'}</Row>
                )}
                <Row label={t('detail.created')}>
                  <time dateTime={detail.createdAt} title={formatDateTime(detail.createdAt)}>
                    {formatRelative(detail.createdAt)}
                  </time>
                </Row>
                {detail.closedAt ? (
                  <Row label={t('detail.closed')}>
                    <time dateTime={detail.closedAt}>{formatDateTime(detail.closedAt)}</time>
                  </Row>
                ) : null}
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>
      {ordering ? (
        <PurchaseOrderDialog
          order={null}
          taskId={detail.id}
          initialLines={linesFromChecklist((detail.checklist ?? []).map((c) => c.label))}
          onClose={() => setOrdering(false)}
        />
      ) : null}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-fg-subtle">{label}</dt>
      <dd className="min-w-0 truncate text-end text-fg">{children}</dd>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <Skeleton className="h-4 w-28" />
      <Skeleton className="h-7 w-72" />
      <div className="grid gap-5 lg:grid-cols-3">
        <Skeleton className="h-64 lg:col-span-2" />
        <Skeleton className="h-64" />
      </div>
    </div>
  );
}
